"use client";

import { useEffect, useState } from "react";
import { useUserStore } from "@/stores/userStore";
// import { showToast } from "@/lib/toast"; // Unused for now
import { usePermissions } from "@/hooks/usePermissions";
import { useDebounce } from "@/hooks/useDebounce";
import { PermissionSubject } from "@/utils/permissions";
import { Button } from "@/components/ui/button";
import { Plus, Search, RefreshCw } from "lucide-react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import PermissionGuard from "@/components/PermissionGuard";
import ModalCreateUser from "@/components/user/ModalCreateUser";
import ModalDeleteUser from "@/components/user/ModalDeleteUser";
import ModalViewUser from "@/components/user/ModalViewUser";
import ModalEditUser from "@/components/user/ModalEditUser";

// Error boundary component
function ErrorBoundary({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export default function UserPage() {
  const {
    users,
    isLoading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchUsers,
    setCurrentPage,
  } = useUserStore();

  const [searchTerm, setSearchTerm] = useState("");
  const [isOpenCreateModal, setIsOpenCreateModal] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isOpenViewModal, setIsOpenViewModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);

  // Không cho phép xóa tài khoản admin gốc
  const PROTECTED_ADMIN_ID = "6604de8ae5068069a1bbb592";

  // Debounce search term để tránh gọi API quá nhiều
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Hiển thị loading khi search term khác với debounced term
  const isSearching = searchTerm !== debouncedSearchTerm;

  // Sử dụng permission hooks
  const { canRead } = usePermissions();

  // Kiểm tra quyền truy cập trang này
  const canAccessPage = canRead(PermissionSubject.USER);

  useEffect(() => {
    // Reset về trang 1 khi search term thay đổi
    setCurrentPage(1);
    // Fetch users data với debounced search term
    fetchUsers({
      pageIndex: 1,
      pageSize: 10,
      search: debouncedSearchTerm,
    });
  }, [fetchUsers, debouncedSearchTerm]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Không cần gọi API ở đây vì đã có useEffect tự động gọi khi debouncedSearchTerm thay đổi
    // Chỉ cần reset về trang 1
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    fetchUsers({
      pageIndex: currentPage,
      pageSize: 10,
      search: debouncedSearchTerm,
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchUsers({
      pageIndex: page,
      pageSize: 10,
      search: debouncedSearchTerm,
    });
  };

  const handleOpenCreateModal = () => {
    setIsOpenCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setIsOpenCreateModal(false);
  };

  const handleOpenDeleteModal = (user: any) => {
    setSelectedUser(user);
    setIsOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setSelectedUser(null);
    setIsOpenDeleteModal(false);
  };

  const handleOpenViewModal = (user: any) => {
    setSelectedUser(user);
    setIsOpenViewModal(true);
  };

  const handleCloseViewModal = () => {
    setSelectedUser(null);
    setIsOpenViewModal(false);
  };

  const handleOpenEditModal = (user: any) => {
    setSelectedUser(user);
    setIsOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setSelectedUser(null);
    setIsOpenEditModal(false);
  };

  // Nếu không có quyền truy cập thì hiển thị fallback
  if (!canAccessPage) {
    return (
      <div className="p-10 text-center">
        <div className="text-red-600 text-lg font-medium mb-2">
          Không có quyền truy cập
        </div>
        <div className="text-gray-500 dark:text-gray-400">
          Bạn không có quyền truy cập trang quản lý người dùng
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <DashboardLayout>
        <div className="p-6">
          <div className="mb-6 flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
                Quản lý người dùng
              </h1>
              <div className="mt-2 flex items-center gap-2">
                <p className="text-gray-500 dark:text-gray-400">
                  Quản lý thông tin người dùng trong hệ thống
                </p>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                  {totalItems} người dùng
                </span>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleRefresh}
                variant="outline"
                className="flex items-center gap-2 hover:cursor-pointer"
                disabled={isLoading}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                />
                Làm mới
              </Button>

              <PermissionGuard subject={PermissionSubject.USER} action="create">
                <Button
                  onClick={handleOpenCreateModal}
                  className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg px-4 py-2 flex items-center gap-2 hover:cursor-pointer"
                >
                  <Plus size={20} />
                  Thêm người dùng
                </Button>
              </PermissionGuard>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mb-6">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Tìm kiếm theo tên hoặc username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {isSearching && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>
              <Button
                type="submit"
                disabled={isLoading || isSearching}
                className="hover:cursor-pointer"
              >
                {isSearching ? "Đang tìm..." : "Tìm kiếm"}
              </Button>
            </form>
          </div>

          {/* Users Table */}
          <div className="shadow-lg rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 overflow-hidden">
            {isLoading || isSearching ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">
                  Đang tải...
                </span>
              </div>
            ) : error ? (
              <div className="text-center py-16">
                <div className="text-red-600 text-lg font-medium mb-2">
                  Lỗi khi tải dữ liệu
                </div>
                <div className="text-gray-500 dark:text-gray-400">{error}</div>
                <Button
                  onClick={handleRefresh}
                  className="mt-4 hover:cursor-pointer"
                >
                  Thử lại
                </Button>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur supports-[backdrop-filter]:bg-gray-50/60 dark:supports-[backdrop-filter]:bg-gray-800/60">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                          Thông tin
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                          Vai trò
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                          Ngày tạo
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Thao tác
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-16">
                            <div className="flex flex-col items-center justify-center text-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                className="h-12 w-12 text-gray-300 mb-3"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="1.5"
                                  d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.5 20.118a7.5 7.5 0 0 1 15 0A17.933 17.933 0 0 1 12 21.75c-2.68 0-5.216-.586-7.5-1.632Z"
                                />
                              </svg>
                              <p className="text-gray-600 font-medium">
                                Chưa có người dùng
                              </p>
                              <p className="text-gray-400 text-sm mt-1">
                                Hãy thêm người dùng đầu tiên để bắt đầu
                              </p>
                              <PermissionGuard
                                subject={PermissionSubject.USER}
                                action="create"
                              >
                                <Button className="mt-4 hover:cursor-pointer">
                                  Thêm người dùng
                                </Button>
                              </PermissionGuard>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        users.map((user) => (
                          <tr
                            key={user._id}
                            className="transition-all hover:bg-gray-50/60 dark:hover:bg-gray-800/60"
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center text-xs font-semibold shadow-sm">
                                  {String(user.name || user.username)
                                    .split(" ")
                                    .map((s) => s[0])
                                    .join("")
                                    .slice(0, 2)
                                    .toUpperCase()}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {user.name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    @{user.username}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ring-1 ring-inset ${
                                  (typeof user.role === "object"
                                    ? user.role?.name
                                    : String(user.role)
                                  )
                                    .toLowerCase()
                                    .includes("admin")
                                    ? "bg-blue-50 text-blue-700 ring-blue-200"
                                    : "bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 ring-gray-200 dark:ring-gray-700"
                                }`}
                              >
                                {typeof user.role === "object"
                                  ? user.role?.name
                                  : user.role}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {user.email || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {user.createdAt
                                ? new Date(user.createdAt).toLocaleDateString(
                                    "vi-VN"
                                  )
                                : "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                <PermissionGuard
                                  subject={PermissionSubject.USER}
                                  action="read"
                                >
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="hover:cursor-pointer"
                                    onClick={() => handleOpenViewModal(user)}
                                  >
                                    Xem
                                  </Button>
                                </PermissionGuard>
                                <PermissionGuard
                                  subject={PermissionSubject.USER}
                                  action="update"
                                >
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="hover:cursor-pointer"
                                    onClick={() => handleOpenEditModal(user)}
                                    disabled={user._id == PROTECTED_ADMIN_ID}
                                  >
                                    Sửa
                                  </Button>
                                </PermissionGuard>
                                <PermissionGuard
                                  subject={PermissionSubject.USER}
                                  action="delete"
                                >
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="text-red-600 hover:text-red-700 hover:cursor-pointer"
                                    onClick={() => handleOpenDeleteModal(user)}
                                    disabled={user._id == PROTECTED_ADMIN_ID}
                                  >
                                    Xóa
                                  </Button>
                                </PermissionGuard>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <Button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        variant="outline"
                        size="sm"
                        className="hover:cursor-pointer"
                      >
                        Trước
                      </Button>
                      <Button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        variant="outline"
                        size="sm"
                        className="hover:cursor-pointer"
                      >
                        Sau
                      </Button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                          Hiển thị{" "}
                          <span className="font-medium">
                            {(currentPage - 1) * 10 + 1}
                          </span>{" "}
                          đến{" "}
                          <span className="font-medium">
                            {Math.min(currentPage * 10, totalItems)}
                          </span>{" "}
                          trong{" "}
                          <span className="font-medium">{totalItems}</span> kết
                          quả
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                          <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            variant="outline"
                            size="sm"
                            className="rounded-l-md hover:cursor-pointer"
                          >
                            Trước
                          </Button>
                          {Array.from(
                            { length: totalPages },
                            (_, i) => i + 1
                          ).map((page) => (
                            <Button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              variant={
                                page === currentPage ? "default" : "outline"
                              }
                              size="sm"
                              className="rounded-none hover:cursor-pointer"
                            >
                              {page}
                            </Button>
                          ))}
                          <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            variant="outline"
                            size="sm"
                            className="rounded-r-md hover:cursor-pointer"
                          >
                            Sau
                          </Button>
                        </nav>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Modals */}
          <ModalCreateUser
            isOpen={isOpenCreateModal}
            onClose={handleCloseCreateModal}
            onComplete={() => {
              handleRefresh();
              handleCloseCreateModal();
            }}
          />

          <ModalDeleteUser
            isOpen={isOpenDeleteModal}
            onClose={handleCloseDeleteModal}
            user={selectedUser}
            onComplete={() => {
              handleRefresh();
              handleCloseDeleteModal();
            }}
          />

          <ModalViewUser
            isOpen={isOpenViewModal}
            onClose={handleCloseViewModal}
            userId={selectedUser?._id || null}
          />

          <ModalEditUser
            isOpen={isOpenEditModal}
            onClose={handleCloseEditModal}
            userId={selectedUser?._id || null}
            onComplete={() => {
              handleRefresh();
              handleCloseEditModal();
            }}
          />
        </div>
      </DashboardLayout>
    </ErrorBoundary>
  );
}
